import React, { useState, useEffect } from 'react'
import { EnterpriseDetail } from '../../../clients/kyousou_api_client'
import { Enterprise, Kuchikomi } from '../../../clients/kuchikomi_api_client'
import mainBgImage from '../../../images/main_bg.png'
import noImage from '../../../images/noimage.png'
import flagIcon from '../../../images/flag_icon.png'
import gridViewIcon from '../../../images/grid_view_icon.png'
import { GivedPointIcon } from '../../../images/gived_point_icon'
import RankItem from '../../top/components/rank_item'
import { EvaluationStarIcon } from '../../../components/common/star_icons'
import { IinePointIcon } from '../../../images/iine_point_icon'
import {
  openKuchikomiEnterpriseDetail,
  openKuchikomiSearch,
} from '../../../utils/kuchikomi_util'
import { useSdgsImage } from '../../../hooks/use_sdgs_image'
import { QuillDisplay } from '../../../components/form/inputs/QuillEditor'

interface EnterprisesDetailTabProps {
  enterprise: EnterpriseDetail
  kuchikomiEnterprise?: Enterprise | null
  kuchikomis?: Kuchikomi[]
}

const EnterprisesDetailTab: React.FC<EnterprisesDetailTabProps> = ({
  enterprise,
  kuchikomiEnterprise,
  kuchikomis = [],
}) => {
  // 共創トップ画像を表示順にソートして取得
  const topImages =
    enterprise.summary?.topImages?.images?.sort(
      (a, b) => a.sortOrder - b.sortOrder
    ) || []

  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  useEffect(() => {
    setSelectedImageIndex(0)
  }, [enterprise.kuchikomiEnterpriseId])

  // kuchikomi側のデータを使用
  const evaluation = kuchikomiEnterprise?.evaluationAverage ?? 0
  const kuchikomiCount = kuchikomiEnterprise?.kuchikomiCount ?? 0
  const givedPointCount = kuchikomiEnterprise?.givedPointCount ?? 0
  const holdPointCount = kuchikomiEnterprise?.holdPointCount ?? 0

  // 企業IDの決定（Kuchikomi側のIDを優先）
  const enterpriseId =
    kuchikomiEnterprise?.id || enterprise.kuchikomiEnterpriseId

  // クチコミ詳細ページへのリンク（クチコミ評価を見る）
  const handleKuchikomiDetailClick = () => {
    openKuchikomiEnterpriseDetail(enterpriseId)
  }

  // クチコミ検索ページへのリンク（その他、クチコミを見る）
  const handleKuchikomiSearchClick = () => {
    openKuchikomiSearch({
      keyword: kuchikomiEnterprise?.name || '',
    })
  }

  const [getSdgsImage] = useSdgsImage()

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        {/* 画像ギャラリーエリア */}
        {topImages.length > 0 && (
          <div className="mb-6">
            <div className="flex gap-4">
              {/* メイン画像 */}
              <div className="flex-1">
                <img
                  src={topImages[selectedImageIndex].imageUrl}
                  alt="企業メイン画像"
                  className="w-full aspect-[4/3] object-cover rounded"
                />
              </div>
              {/* サムネイル画像 */}
              <div className="flex flex-col gap-3">
                {topImages.slice(0, 7).map((image, index) => (
                  <div
                    key={index}
                    className="w-32 h-24 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img
                      src={image.imageUrl || mainBgImage}
                      alt={`サムネイル画像${index + 1}`}
                      className={`w-full h-full object-cover rounded ${
                        selectedImageIndex === index
                          ? 'ring-2 ring-main-green'
                          : ''
                      }`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {/* クチコミ評価セクション */}
        <h3 className="text-xl font-bold mb-6">クチコミ評価</h3>
        <div className="flex items-center gap-2 mb-4">
          <EvaluationStarIcon evaluation={evaluation} size="lg" />
          <p className="text-3xl font-bold relative top-[2px]">
            {evaluation.toFixed(1)}
          </p>
          <span className="px-2"></span>
          <p>
            クチコミ件数：
            {kuchikomiCount.toLocaleString()}件
          </p>
        </div>
        <div className="flex items-center gap-6 mb-4">
          <div className="flex items-center gap-1">
            <GivedPointIcon size="sm" />
            <span className="text-main-green text-sm relative top-[1px]">
              ポイント贈呈数：
              {givedPointCount.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <GivedPointIcon size="sm" />
            <span className="text-main-green text-sm relative top-[1px]">
              ポイント保有数：
              {holdPointCount.toLocaleString()}
            </span>
          </div>
        </div>
        <p className="text-sm mb-6">{kuchikomiEnterprise?.description}</p>
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* kuchikomi側のランキングデータがある場合はそれを使用 */}
          {kuchikomiEnterprise ? (
            <>
              {kuchikomiEnterprise.enterpriseCode &&
                kuchikomiEnterprise.enterpriseRank && (
                  <RankItem
                    title={kuchikomiEnterprise.enterpriseCode.value}
                    rank={kuchikomiEnterprise.enterpriseRank}
                    label="団体別クチコミランキング"
                  />
                )}
              {kuchikomiEnterprise.industryParentCode &&
                kuchikomiEnterprise.industryRank && (
                  <RankItem
                    title={kuchikomiEnterprise.industryParentCode.value}
                    rank={kuchikomiEnterprise.industryRank}
                    label="業界別クチコミランキング"
                  />
                )}
              {kuchikomiEnterprise.prefecture &&
                kuchikomiEnterprise.prefectureRank && (
                  <RankItem
                    title={kuchikomiEnterprise.prefecture}
                    rank={kuchikomiEnterprise.prefectureRank}
                    label="都道府県別クチコミランキング"
                  />
                )}
              {kuchikomiEnterprise.sdgsRank &&
                kuchikomiEnterprise.sdgsRank.length > 0 && (
                  <RankItem
                    title=""
                    rank={kuchikomiEnterprise.sdgsRank[0]}
                    label="SDGs クチコミランキング"
                  />
                )}
            </>
          ) : (
            // kuchikomi側のデータがない場合は何も表示しない
            <div className="col-span-2 text-center text-main-darkgray py-4">
              ランキングデータがありません
            </div>
          )}
        </div>
        <button
          onClick={handleKuchikomiDetailClick}
          className="bg-main-white text-main-green border border-main-green px-6 py-3 rounded hover:bg-main-lightgreen transition-colors font-medium w-full"
        >
          クチコミ評価を見る
        </button>

        {/* 新着クチコミセクション */}
        {kuchikomis && kuchikomis.length > 0 && (
          <>
            <h3 className="text-xl font-bold mb-6 border-t border-black pt-8">
              新着クチコミ
            </h3>
            <div className="border border-main-lightgray rounded p-6">
              <div className="space-y-0">
                {kuchikomis.map((kuchikomi, i: number) => (
                  <div key={i}>
                    <div className="mb-4">
                      <div className="flex justify-between">
                        <div className="flex items-center gap-2">
                          <img
                            src={kuchikomi.reviewer.iconUrl}
                            alt="ユーザーアイコン"
                            className="w-6 h-6 rounded-full object-contain"
                          />
                          <span className="font-bold">
                            {kuchikomi.reviewer.displayName}
                          </span>
                        </div>
                        <div>
                          <EvaluationStarIcon evaluation={5} size="md" />
                        </div>
                      </div>
                      <div>
                        <p className="text-main-darkgray leading-relaxed">
                          {kuchikomi.kuchikomi}
                        </p>
                      </div>
                      <div className="flex justify-between mt-3">
                        <div className="flex items-center gap-1">
                          <IinePointIcon size="sm" />
                          <span className="text-main-green text-sm relative top-[1px]">
                            {kuchikomi.likeCount}
                          </span>
                        </div>
                        {kuchikomi.category && (
                          <div className="flex items-center gap-2">
                            <img
                              src={getSdgsImage(kuchikomi.category.code)}
                              alt={kuchikomi.category.value}
                              className="w-6 h-6 object-contain"
                            />
                            <p className="text-xs text-main-darkgray">
                              {kuchikomi.category.value}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    {i < kuchikomis.length - 1 && (
                      <div className="border-b border-black mb-4"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center mt-6">
              <button
                onClick={handleKuchikomiSearchClick}
                className="bg-main-white text-main-green border border-main-green px-6 py-3 rounded hover:bg-main-lightgreen transition-colors font-medium"
              >
                その他、クチコミを見る
              </button>
            </div>
          </>
        )}
      </div>

      {/* 企業概要セクション */}
      {enterprise.summary?.companyProfile && (
        <div>
          <h3 className="text-xl font-bold my-6 flex items-center gap-2">
            企業概要
          </h3>
          <p>{enterprise.summary.companyProfile}</p>
        </div>
      )}

      {/* 自社特徴セクション */}
      {enterprise.summary?.companyFeature && (
        <div className="border-y border-black pb-8 pt-6">
          <div className="flex items-center gap-2 mb-6">
            <img src={flagIcon} className="w-5 h-5" />
            <h3 className="text-xl font-bold">自社特徴</h3>
          </div>
          <QuillDisplay content={enterprise.summary.companyFeature || ''} />
        </div>
      )}

      {/* 提供リソースセクション */}
      {enterprise.summary?.providedResource && (
        <div className="border-b border-black pb-8">
          <div className="flex items-center gap-2 mb-6">
            <img src={gridViewIcon} className="w-5 h-5" />
            <h3 className="text-xl font-bold">提供リソース</h3>
          </div>
          <QuillDisplay content={enterprise.summary.providedResource || ''} />
        </div>
      )}

      {/* 主要顧客セクション */}
      {enterprise.summary?.majorCustomer && (
        <div className="border-b border-main-black pb-8">
          <h3 className="text-xl font-bold mb-6">主要顧客</h3>
          <QuillDisplay content={enterprise.summary.majorCustomer || ''} />
        </div>
      )}

      {/* 株主セクション */}
      {enterprise.summary?.shareholder && (
        <div className="border-b border-main-black pb-8">
          <h3 className="text-xl font-bold mb-6">株主</h3>
          <QuillDisplay content={enterprise.summary.shareholder || ''} />
        </div>
      )}

      {/* オープンイノベーション実績セクション */}
      {enterprise.summary?.openInnovationAchievements?.achievements &&
        enterprise.summary.openInnovationAchievements.achievements.length >
          0 && (
          <div className="border-b border-black pb-8">
            <h3 className="text-xl font-bold mb-6">
              オープンイノベーション実績
            </h3>
            <div className="space-y-6">
              {enterprise.summary.openInnovationAchievements.achievements.map(
                (achievement) => (
                  <div
                    key={`achievement-${achievement.id}`}
                    className="relative"
                  >
                    <div className="flex gap-4">
                      {/* 左側の画像 */}
                      <div className="flex-shrink-0">
                        <img
                          src={achievement.imageUrl || noImage}
                          alt="オープンイノベーション実績画像"
                          className="w-32 h-32 object-cover rounded mb-4"
                        />
                      </div>
                      {/* 右側のテキストコンテンツ */}
                      <div className="flex-1">
                        <QuillDisplay content={achievement.content || ''} />
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}
    </div>
  )
}

export default EnterprisesDetailTab
